import React, { useMemo } from "react";
import { InternalContent } from "@components/library";
import { useTranslation } from "@helpers/translate";
import { Helmet } from "react-helmet";
import MarketsTable from "../components/MarketsTable/index.jsx";
import {
  useMarketsTableData,
  MARKETS_TABLE_CATEGORY,
} from "../components/MarketsTable/hooks/useMarketsTableData";
import { formatCompact } from "../components/MarketsTable/utils/formatters";
import "./index.sass";
import fireIcon from "@assets/images/icons/new-listings-fire-icon.svg";
import coinsIcon from "@assets/images/icons/new-listings-coins-icon.svg";
import coinIcon from "@assets/images/icons/new-listings-coin-icon.svg";
import trendUpCircleIcon from "@assets/images/icons/new-listings-trendup-circle-icon.svg";
import trendUpIcon from "@assets/images/icons/new-listings-trendup-icon.svg";
import trendDownIcon from "@assets/images/icons/new-listings-trenddown-icon.svg";
import trendDownCircleIcon from "@assets/images/icons/new-listings-trenddown-circle-icon.svg";
import chartIcon from "@assets/images/icons/new-listings-chartbar-icon.svg";
import bitcoinIcon from "@assets/images/icons/new-listings-bitcoin-icon.svg";
import rotateIcon from "@assets/images/icons/new-listings-rotate-icon.svg";
import ethIcon from "@assets/images/icons/new-listings-eth-icon.svg";

export const NewListings = () => {
  const { __ } = useTranslation();
  const { coins, isLoading } = useMarketsTableData(MARKETS_TABLE_CATEGORY.all);
  const [selectedCardId, setSelectedCardId] = React.useState(null);

  const cardsData = useMemo(() => {
    if (!coins.length || isLoading) {
      return [
        {
          id: 'hot-crypto',
          title: 'Hot crypto',
          icon: fireIcon,
          contentIcon: coinsIcon,
          value: '0/0',
          suffix: '%',
          type: 'percentage',
          isNegative: false
        },
        {
          id: 'new-listings',
          title: 'New Listings',
          icon: coinIcon,
          contentIcon: coinsIcon,
          value: new Date().toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric'
          }),
          type: 'date',
          isNegative: false
        },
        {
          id: 'top-gainers',
          title: 'Top gainers',
          icon: trendUpIcon,
          contentIcon: trendUpCircleIcon,
          value: '0.00',
          suffix: '%',
          type: 'percentage',
          isNegative: false
        },
        {
          id: 'top-losers',
          title: 'Top losers',
          icon: trendDownIcon,
          contentIcon: trendDownCircleIcon,
          value: '0.00',
          suffix: '%',
          type: 'percentage',
          isNegative: false
        },
        {
          id: 'largest-market-cap',
          title: 'Largest market cap',
          icon: chartIcon,
          contentIcon: bitcoinIcon,
          value: '0',
          type: 'marketcap',
          isNegative: false
        },
        {
          id: 'highest-turnover',
          title: 'Highest turnover',
          icon: rotateIcon,
          contentIcon: ethIcon,
          value: '0',
          type: 'marketcap',
          isNegative: false
        }
      ];
    }

    const sortedByChange = [...coins].sort((a, b) => 
      (b.price_change_percentage_24h || 0) - (a.price_change_percentage_24h || 0)
    );
    
    const sortedByVolume = [...coins].sort((a, b) => 
      (b.total_volume || 0) - (a.total_volume || 0)
    );
    
    const sortedByMarketCap = [...coins].sort((a, b) => 
      (b.market_cap || 0) - (a.market_cap || 0)
    );

    const topGainer = sortedByChange[0];
    const topLoser = sortedByChange[sortedByChange.length - 1];
    const largestMarketCap = sortedByMarketCap[0];
    const highestTurnover = sortedByVolume[0];

    const hotCryptoCount = coins.filter(coin => 
      (coin.price_change_percentage_24h || 0) > 10
    ).length;

    const totalCoins = coins.length;
    const currentDate = new Date().toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    });

    return [
      {
        id: 'hot-crypto',
        title: 'Hot crypto',
        icon: fireIcon,
        contentIcon: coinsIcon,
        value: `${hotCryptoCount}/${totalCoins}`,
        suffix: '%',
        type: 'percentage',
        isNegative: false
      },
      {
        id: 'new-listings',
        title: 'New Listings',
        icon: coinIcon,
        contentIcon: coinsIcon,
        value: currentDate,
        type: 'date',
        isNegative: false
      },
      {
        id: 'top-gainers',
        title: 'Top gainers',
        icon: trendUpIcon,
        contentIcon: trendUpCircleIcon,
        value: topGainer?.price_change_percentage_24h?.toFixed(2) || '0.00',
        suffix: '%',
        type: 'percentage',
        isNegative: false
      },
      {
        id: 'top-losers',
        title: 'Top losers',
        icon: trendDownIcon,
        contentIcon: trendDownCircleIcon,
        value: topLoser?.price_change_percentage_24h?.toFixed(2) || '0.00',
        suffix: '%',
        type: 'percentage',
        isNegative: (topLoser?.price_change_percentage_24h || 0) < 0
      },
      {
        id: 'largest-market-cap',
        title: 'Largest market cap',
        icon: chartIcon,
        contentIcon: bitcoinIcon,
        value: largestMarketCap?.market_cap 
          ? formatCompact(largestMarketCap.market_cap) 
          : '0',
        type: 'marketcap',
        isNegative: false
      },
      {
        id: 'highest-turnover',
        title: 'Highest turnover',
        icon: rotateIcon,
        contentIcon: ethIcon,
        value: highestTurnover?.total_volume 
          ? formatCompact(highestTurnover.total_volume) 
          : '0',
        type: 'marketcap',
        isNegative: false
      }
    ];
  }, [coins, isLoading]);

  return (
    <InternalContent>
      <Helmet>
        <title>
          New Listings {__("seo.divider")} {__("seo.title")}
        </title>
      </Helmet>

      <div className="new-listings">
        <div className="new-listings__header">
          <h1 className="new-listings__title">New Listings</h1>
        </div>
        
        <div className="new-listings__cards">
          <ul>
            {cardsData.map((card) => (
              <li 
                key={card.id}
                className={selectedCardId === card.id ? 'new-listings__card-item--active' : ''}
                onClick={() => setSelectedCardId(card.id)}
              >
                <div className="new-listings__card">
                  <div className="new-listings__card-header">
                    <h6 className="new-listings__card-title">{card.title}</h6>
                    <img src={card.icon} alt={card.title} />
                  </div>
                </div>
                <div className="new-listings__card-content">
                  <img src={card.contentIcon} alt={`${card.title} icon`} />
                  <span 
                    className={`new-listings__card-content-${card.type} ${
                      card.isNegative ? 'new-listings__card-content--negative' : ''
                    }`}
                  >
                    {card.value}{card.suffix || ''}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </div>
        
        <div className="new-listings__content">
          <MarketsTable
            defaultCategory={MARKETS_TABLE_CATEGORY.newListings}
            hideTitle={true}
          />
        </div>
      </div>
    </InternalContent>
  );
};

export default NewListings;
